import React, { useEffect, useState } from "react";
import { useLang } from "@/i18n/LanguageContext";

const CATS = [
  { key: "problem", label: "PROBLEM", max: 20 },
  { key: "customer", label: "CUSTOMER", max: 15 },
  { key: "market", label: "MARKET", max: 20 },
  { key: "competition", label: "COMPETITION", max: 10 },
  { key: "offer", label: "OFFER", max: 10 },
  { key: "demand", label: "DEMAND", max: 15 },
  { key: "validation", label: "VALIDATION", max: 10 },
];

export const COMP_COLORS = { clarity: "#7c3aed", evidence: "#3b82f6", coherence: "#a78bfa", validation: "#06b6d4" };
export const COMP_KEYS = ["clarity", "evidence", "coherence", "validation"];

export function bandColor(band) {
  switch (band) {
    case "GO": case "STRONG CLARITY": return "#16a34a";
    case "PROMISING": return "#7c3aed";
    case "KEEP TESTING": return "#3b82f6";
    default: return "#f97316"; // NEEDS REWORK
  }
}

/** Human label for a band (new CCI bands + legacy names). */
export function useBandLabel() {
  const { t } = useLang();
  return (band) => { if (!band) return ""; const v = t(`cci.bands.${band}`); return v.startsWith("cci.bands.") ? band : v; };
}

export function ScoreRadial({ total = 0, band = "", size = 220, animate = true, showBand = true }) {
  const bandLabel = useBandLabel();
  const [display, setDisplay] = useState(animate ? 0 : total);
  const stroke = size < 100 ? 7 : 14;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const color = bandColor(band);

  useEffect(() => {
    if (!animate) { setDisplay(total); return; }
    let raf;
    const start = performance.now();
    const dur = 1200;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(eased * total));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [total, animate]);

  const offset = c - (display / 100) * c;

  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }} data-testid="score-radial-visualizer">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#eef1f7" strokeWidth={stroke} />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke}
          strokeLinecap="round" strokeDasharray={c} strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset 0.1s linear" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`${size < 100 ? "text-2xl" : size < 160 ? "text-4xl" : "text-5xl"} font-bold tracking-tight text-[#0b0f1a] leading-none`} data-testid="score-total">{display}</span>
        {size >= 100 && <span className="text-[11px] tracking-widest text-slate-400 mt-1">/ 100</span>}
        {band && size >= 160 && (
          <span className="mt-2 text-xs font-semibold tracking-wider px-3 py-1 rounded-full whitespace-nowrap" style={{ color, background: `${color}14` }}>
            {bandLabel(band)}
          </span>
        )}
      </div>
      {band && showBand && size < 160 && (
        <span className="absolute left-1/2 -translate-x-1/2 -bottom-3 text-[10px] font-semibold tracking-wider px-2.5 py-0.5 rounded-full whitespace-nowrap" style={{ color, background: `${color}14` }} data-testid="score-band-outside">
          {bandLabel(band)}
        </span>
      )}
    </div>
  );
}

export function ScoreBreakdown({ breakdown = {}, weakest }) {
  const { t } = useLang();
  return (
    <div className="space-y-3" data-testid="score-breakdown">
      {CATS.map((cat) => {
        const val = breakdown[cat.key] ?? 0;
        const pct = (val / cat.max) * 100;
        const isWeak = weakest === cat.key;
        return (
          <div key={cat.key}>
            <div className="flex justify-between items-baseline mb-1">
              <span className={`text-[11px] font-semibold tracking-wider ${isWeak ? "text-[#f97316]" : "text-slate-500"}`}>
                {t(`report.cat.${cat.key}`).toUpperCase()}{isWeak ? " ·" : ""}
              </span>
              <span className="text-xs font-semibold text-[#0b0f1a]">{val}/{cat.max}</span>
            </div>
            <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{ width: `${pct}%`, background: isWeak ? "#f97316" : "linear-gradient(90deg,#7c3aed,#3b82f6)" }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
